import { Button, Group, Stack, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import React from 'react';
import { parseGoogleStreetViewUrl } from '../../../utils/googleStreetViewUrl.ts';

export interface MutatePlaceFormValues {
  name: string;
  address: string;
  googleMapStreetViewUrl: string;
  notes?: string;
}

interface MutatePlaceFormProps {
  initialValues: MutatePlaceFormValues;
  onSubmit: (formValues: MutatePlaceFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
}

export default function MutatePlaceForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitButtonText,
}: MutatePlaceFormProps) {
  const form = useForm<MutatePlaceFormValues>({
    mode: 'uncontrolled',
    initialValues: {
      name: initialValues.name,
      address: initialValues.address,
      googleMapStreetViewUrl: initialValues.googleMapStreetViewUrl,
      notes: initialValues.notes,
    },
    validate: {
      name: (value) => {
        if (!value.trim()) return ' ';
      },
      address: (value) => {
        if (!value.trim()) return ' ';
      },
      googleMapStreetViewUrl: (value) => {
        if (!value.trim()) return ' ';
        try {
          new URL(value);
        } catch {
          return '無效的 URL 格式';
        }
        try {
          parseGoogleStreetViewUrl(value);
        } catch (error) {
          return error instanceof Error ? error.message : '街景網址格式錯誤';
        }
      },
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          {/* 地點名稱 */}
          <TextInput
            label={'地點名稱'}
            placeholder={'請輸入地點名稱'}
            withAsterisk
            key={form.key('name')}
            {...form.getInputProps('name')}
            styles={{
              label: { marginBottom: 8 },
              input: {
                paddingLeft: 10,
                borderColor: form.errors.name
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
          {/* 地點地址 */}
          <TextInput
            label={'地點地址'}
            placeholder={'請輸入地點地址'}
            withAsterisk
            key={form.key('address')}
            {...form.getInputProps('address')}
            styles={{
              label: { marginBottom: 8 },
              input: {
                paddingLeft: 10,
                borderColor: form.errors.address
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
          {/* Google Map 街景網址 */}
          <TextInput
            label={'Google Map 街景網址'}
            placeholder={'請輸入 Google Map 街景網址'}
            withAsterisk
            key={form.key('googleMapStreetViewUrl')}
            {...form.getInputProps('googleMapStreetViewUrl')}
            styles={{
              label: { marginBottom: 8 },
              input: {
                paddingLeft: 10,
                borderColor: form.errors.googleMapStreetViewUrl
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
          {/* 小編精選 */}
          <Textarea
            label={'小編精選'}
            key={form.key('notes')}
            {...form.getInputProps('notes')}
            rows={4}
            styles={{
              label: { marginBottom: 8 },
              input: {
                paddingLeft: 10,
                borderColor: form.errors.notes
                  ? 'var(--mantine-color-red-6)'
                  : undefined,
              },
            }}
          />
        </Stack>

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={handleCancel}>
            取消
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {submitButtonText}
          </Button>
        </Group>
      </form>
    </>
  );
}
